import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@loot-os/ui/components/alert-dialog";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Plus } from "lucide-react";
import { useState } from "react";

import { useSessionQuery } from "../lib/authQueries";
import { formatDate } from "../lib/format";
import { useTRPC } from "../lib/trpc";

function CopyLinkButton({ link }: { link: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={() => {
        void navigator.clipboard.writeText(link);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? <Check /> : <Copy />}
      {copied ? "Copied" : "Copy link"}
    </Button>
  );
}

/** What grant returned: an account upgraded in place, or an invitation with its accept link. */
interface GrantResult {
  email: string;
  emailed: boolean;
  /** The accept link, when the address had no account and was invited instead. */
  link?: string;
}

export function PlatformAdminsPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { data: session } = useSessionQuery();

  const admins = useQuery(trpc.platform.admins.list.queryOptions());
  const invites = useQuery(trpc.platform.admins.listInvites.queryOptions());

  const [showAdd, setShowAdd] = useState(false);
  const [email, setEmail] = useState("");
  const [granted, setGranted] = useState<GrantResult | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: trpc.platform.admins.list.queryOptions().queryKey,
    });
    void queryClient.invalidateQueries({
      queryKey: trpc.platform.admins.listInvites.queryOptions().queryKey,
    });
  };

  const grant = useMutation(
    trpc.platform.admins.grant.mutationOptions({
      onSuccess: (result) => {
        invalidate();
        setGranted({
          email: result.email,
          emailed: result.emailed,
          link: result.invited ? result.link : undefined,
        });
        setEmail("");
      },
    }),
  );
  const revoke = useMutation(
    trpc.platform.admins.revoke.mutationOptions({ onSuccess: invalidate }),
  );
  const cancelInvite = useMutation(
    trpc.platform.admins.cancelInvite.mutationOptions({ onSuccess: invalidate }),
  );

  const closeAdd = () => {
    setShowAdd(false);
    setGranted(null);
    grant.reset();
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Platform admins</h1>
          <p className="text-sm text-muted-foreground">
            Who can create tenants, edit the shared catalog, and act on any tenant. This is the
            highest level of access — grant it sparingly.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => {
            grant.reset();
            setGranted(null);
            setShowAdd(true);
          }}
        >
          <Plus />
          Add platform admin
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Admins</CardTitle>
          <CardDescription>
            Revoking removes platform access only — the account itself stays.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Admin</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className="text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.data?.map((admin) => {
                const isSelf = admin.id === session?.user.id;
                return (
                  <TableRow key={admin.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {admin.name || admin.email}
                          {isSelf && (
                            <span className="ml-2 text-xs text-muted-foreground">you</span>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground">{admin.email}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(admin.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      {/* Self-revoke is refused server-side; don't offer it. */}
                      {!isSelf && (
                        <AlertDialog>
                          <AlertDialogTrigger
                            render={
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                disabled={revoke.isPending}
                              />
                            }
                          >
                            Revoke
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Revoke platform access for {admin.name || admin.email}?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                They immediately lose the tenant list, the catalog, and the ability
                                to act on tenants they aren't a member of. Their account and any
                                tenant memberships stay.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => revoke.mutate({ userId: admin.id })}
                              >
                                Revoke access
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pending invitations</CardTitle>
          <CardDescription>
            Addresses with no account yet. Each link is where they set their password; adding the
            same email again replaces its link.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {invites.data?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-muted-foreground">
                    No pending invitations.
                  </TableCell>
                </TableRow>
              )}
              {invites.data?.map((invite) => {
                const expired = new Date(invite.expiresAt).getTime() < Date.now();
                return (
                  <TableRow key={invite.id}>
                    <TableCell className="font-medium">{invite.email}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(invite.expiresAt)}
                      {expired && <span className="ml-2 text-xs text-destructive">expired</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-2">
                        {!expired && <CopyLinkButton link={invite.link} />}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={cancelInvite.isPending}
                          onClick={() => cancelInvite.mutate({ invitationId: invite.id })}
                        >
                          Cancel
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showAdd} onOpenChange={(open) => !open && closeAdd()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a platform admin</DialogTitle>
            <DialogDescription>
              An existing account is upgraded in place and emailed a sign-in link. An email with no
              account gets an invitation: a link, emailed and shown here, where they choose their
              own password.
            </DialogDescription>
          </DialogHeader>
          {granted ? (
            <div className="flex min-w-0 flex-col gap-3">
              {granted.link ? (
                <>
                  <p className="text-sm">
                    <span className="font-mono">{granted.email}</span> has been invited.{" "}
                    {granted.emailed
                      ? "They've been emailed this link, and you can share it directly too:"
                      : "The email couldn't be sent, so share this link with them yourself:"}
                  </p>
                  <div className="flex min-w-0 items-center gap-2 rounded-md border bg-muted/50 px-3 py-2">
                    <code className="min-w-0 flex-1 truncate font-mono text-xs">
                      {granted.link}
                    </code>
                    <div className="shrink-0">
                      <CopyLinkButton link={granted.link} />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    They set their password there and the account is created as a platform admin.
                    The link works for 7 days.
                  </p>
                </>
              ) : (
                <p className="text-sm">
                  <span className="font-mono">{granted.email}</span> is now a platform admin.{" "}
                  {granted.emailed
                    ? "They've been emailed a sign-in link."
                    : `The email couldn't be sent — let them know they can sign in at ${window.location.origin}/login.`}
                </p>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setGranted(null)}>
                  Add another
                </Button>
                <Button type="button" onClick={closeAdd}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form
              className="grid gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                grant.mutate({ email: email.trim() });
              }}
            >
              <div className="grid gap-2">
                <Label htmlFor="admin-email">Email</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="teammate@lootlabs.xyz"
                  required
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeAdd}>
                  Cancel
                </Button>
                <Button type="submit" disabled={grant.isPending || !email.trim()}>
                  {grant.isPending ? "Adding…" : "Add admin"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
